import React from 'react';
import { ArrowLeft, ArrowRight, Eye, EyeOff, Home, LayoutGrid, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  busy: boolean;
  canGoPrevious: boolean;
  canGoNext: boolean;
  isOverview: boolean;
  isBlackout: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onToggleOverview: () => void;
  onGoToTheme: () => void;
  onToggleBlackout: () => void;
  onEnd: () => void;
}

/**
 * Bediening van een live canvas-sessie: alleen betrouwbare pad-navigatie
 * (Vorige/Volgende/Overzicht/Thema/Blackout) -- geen vrij scrollen/slepen of
 * klik-navigatie (navigatie-besluit #3): dat risico (spreker raakt de
 * framing kwijt tijdens een dienst) is een aparte, latere beslissing.
 * Bewust een nieuwe sibling van PresentationControls i.p.v. een uitbreiding
 * ervan -- een écht andere knoppenset, geen incrementele toevoeging.
 */
export default function CanvasPresentationControls({
  busy, canGoPrevious, canGoNext, isOverview, isBlackout,
  onPrevious, onNext, onToggleOverview, onGoToTheme, onToggleBlackout, onEnd,
}: Props) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <Button variant="secondary" disabled={busy || !canGoPrevious} onClick={onPrevious}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Vorige
        </Button>
        <Button disabled={busy || !canGoNext} onClick={onNext}>
          Volgende <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Button variant={isOverview ? 'default' : 'outline'} disabled={busy} onClick={onToggleOverview}>
          <LayoutGrid className="mr-2 h-4 w-4" /> Overzicht
        </Button>
        <Button variant="outline" disabled={busy} onClick={onGoToTheme}>
          <Home className="mr-2 h-4 w-4" /> Thema
        </Button>
      </div>

      <Button variant={isBlackout ? 'default' : 'outline'} disabled={busy} onClick={onToggleBlackout} className="w-full">
        {isBlackout ? <Eye className="mr-2 h-4 w-4" /> : <EyeOff className="mr-2 h-4 w-4" />}
        {isBlackout ? 'Zwart scherm uit' : 'Zwart scherm aan'}
      </Button>

      <Button variant="ghost" className="w-full text-red-400 hover:text-red-300" disabled={busy} onClick={onEnd}>
        <Square className="mr-2 h-4 w-4" /> Sessie beëindigen
      </Button>
    </div>
  );
}
